// src/components/RecordingScreen.tsx
import React, { useState, useEffect, useRef } from 'react';

interface RecordingScreenProps {
  onStop: () => void;
}

const RecordingScreen: React.FC<RecordingScreenProps> = ({ onStop }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [animationTime, setAnimationTime] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  // Fade in animation on mount and lock body scroll
  useEffect(() => {
    // Lock body scroll
    document.body.classList.add('recording-active');
    document.body.style.overflow = 'hidden';
    
    // Trigger fade-in after a brief delay
    const timer = setTimeout(() => setIsVisible(true), 10);
    
    return () => {
      clearTimeout(timer);
      // Unlock body scroll on unmount
      document.body.classList.remove('recording-active');
      document.body.style.overflow = '';
    };
  }, []);

  // Audio level animation and duration tracking
  useEffect(() => {
    startTimeRef.current = Date.now();
   
    const animate = () => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
     
      // Smooth, continuous wave patterns with multiple frequencies
      const wave1 = Math.sin(elapsed * 1.2) * 0.25;
      const wave2 = Math.sin(elapsed * 0.8) * 0.2;
      const wave3 = Math.sin(elapsed * 1.5) * 0.15;
      const wave4 = Math.sin(elapsed * 0.5) * 0.1;
      const newLevel = 0.4 + wave1 + wave2 + wave3 + wave4;
     
      setAudioLevel(Math.max(0.1, Math.min(0.95, newLevel)));
      setAnimationTime(elapsed);
      setRecordingDuration(Math.floor(elapsed));
     
      animationFrameRef.current = requestAnimationFrame(animate);
    };
   
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStop = () => {
    setIsVisible(false);
    // Wait for fade-out animation before calling onStop
    setTimeout(() => {
      onStop();
    }, 300);
  };

  const CircularWave: React.FC<{ delay: number; scale: number; opacity: number }> = ({ 
    delay, 
    scale, 
    opacity 
  }) => {
    const baseScale = 1 + audioLevel * scale;
    const baseOpacity = opacity * (1 - audioLevel * 0.3);

    return (
      <div
        className="circular-wave-shell"
        style={{
          borderColor: `rgba(16, 43, 116, ${baseOpacity})`,
          transform: `scale(${baseScale})`,
          transitionDelay: `${delay}ms`,
        }}
      />
    );
  };

  const ParticleRing: React.FC<{ count: number; radius: number; color: string }> = ({ 
    count, 
    radius, 
    color 
  }) => {
    return (
      <>
        {Array.from({ length: count }).map((_, i) => {
          const angle = (i / count) * Math.PI * 2;
          const distance = radius * (1 + audioLevel * 0.5);
          const x = Math.cos(angle + animationTime * 0.5) * distance;
          const y = Math.sin(angle + animationTime * 0.5) * distance;
          const particleOpacity = 0.6 + audioLevel * 0.4;

          return (
            <div
              key={i}
              className="particle"
              style={{
                backgroundColor: color,
                opacity: particleOpacity,
                left: '50%',
                top: '50%',
                transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                boxShadow: `0 0 10px ${color}`,
              }}
            />
          );
        })}
      </>
    );
  };

  const WaveBar: React.FC<{ index: number; total: number }> = ({ index, total }) => {
    const height = 20 + audioLevel * 50 + Math.sin(animationTime * 2.5 + index * 0.8) * 15;

    return (
      <div
        className="wave-bar"
        style={{
          height: `${height}px`,
          backgroundColor: `rgba(16, 43, 116, ${0.8 + audioLevel * 0.2})`,
          boxShadow: '0 0 10px #112a74',
        }}
      />
    );
  };

  return (
    <div className={`recording-overlay ${isVisible ? "visible" : ""}`}>
    <div 
      className="audio-visualizer-overlay"
      style={{ opacity: isVisible ? 1 : 0 }}
    >
      <div className="audio-visualizer-container">
        {/* Main circular visualization */}
        <div className="visualizer-circle">
          {/* Outer glow effect */}
          <div
            className="visualizer-glow"
            style={{
              background: `radial-gradient(circle, rgba(17, 24, 39, ${0.15 + audioLevel * 0.1}) 0%, transparent 70%)`,
              transform: `scale(${1.2 + audioLevel * 0.3})`,
            }}
          />

          {/* Animated circular waves */}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'relative', width: '208px', height: '208px' }}>
              <CircularWave delay={0} scale={0.2} opacity={0.4} />
              <CircularWave delay={100} scale={0.4} opacity={0.3} />
              <CircularWave delay={200} scale={0.6} opacity={0.2} />
              <CircularWave delay={300} scale={0.8} opacity={0.1} />
            </div>
          </div>

          {/* Particle rings */}
          <div style={{ position: 'absolute', inset: 0 }}>
            <ParticleRing count={12} radius={80} color="rgba(25, 59, 153, 1)" />
            <ParticleRing count={8} radius={105} color="rgba(16, 43, 116, 1)" />
          </div>

          {/* Center orb */}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div
              className="center-orb"
              style={{
                background: 'radial-gradient(circle, rgba(16, 43, 116, 1) 0%, rgba(25, 59, 153, 0.8) 100%)',
                boxShadow: `0 0 ${40 + audioLevel * 60}px rgba(99, 102, 241, ${0.6 + audioLevel * 0.4}),
                   inset 0 0 ${20 + audioLevel * 30}px rgba(16, 43, 116, 1)`,
                transform: `scale(${1 + audioLevel * 0.1})`,
              }}
            >
              {/* Animated pulse rings around the orb */}
              <div
                className="orb-ring"
                style={{
                  borderColor: `rgba(20, 41, 179, ${0.6 - (Math.sin(animationTime * 2) * 0.3)})`,
                  transform: `scale(${1 + Math.sin(animationTime * 2) * 0.3})`,
                }}
              />
              <div
                className="orb-ring"
                style={{
                  borderColor: `rgba(4, 55, 184, ${0.5 - (Math.sin(animationTime * 2 + 1) * 0.3)})`,
                  transform: `scale(${1 + Math.sin(animationTime * 2 + 1) * 0.4})`,
                }}
              />
              <div
                className="orb-ring"
                style={{
                  borderColor: `rgba(3, 42, 140, ${0.4 - (Math.sin(animationTime * 2 + 2) * 0.2)})`,
                  transform: `scale(${1 + Math.sin(animationTime * 2 + 2) * 0.5})`,
                }}
              />

              {/* Inner pulse */}
              <div
                className="inner-orb-flare"
                style={{
                  background: 'radial-gradient(circle, rgba(255, 255, 255, 0.23) 0%, transparent 70%)',
                  transform: `scale(${1 + audioLevel * 0.3})`,
                  opacity: 0.8,
                }}
              />

              {/* Microphone icon */}
              <div className="mic-icon">
                <svg
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    color: 'white',
                    transform: `scale(${1 + audioLevel * 0.15})`,
                    transition: 'transform 500ms ease-out'
                  }}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Wave bars visualization */}
        <div className="wave-bars">
          {Array.from({ length: 5 }).map((_, i) => (
            <WaveBar key={i} index={i} total={5} />
          ))}
        </div>

        {/* Stop button */}
        <button
          onClick={handleStop}
          style={{
            position: 'relative',
            padding: '16px 32px',
            background: 'linear-gradient(to right, #ec4899, #be185d)',
            borderRadius: '9999px',
            fontWeight: 600,
            color: 'white',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            transition: 'all 0.3s ease',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <span style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ width: '12px', height: '12px', background: 'white', borderRadius: '2px' }} />
            Stop Recording
          </span>
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to right, #be185d, #9d174d)',
            opacity: 0,
            transition: 'opacity 0.3s'
          }} />
        </button>

        {/* Status text */}
        <div className="rec-status">
          <p className="rec-status-main">
            CareShift is listening...
          </p>
          <p className="rec-status-sub" style={{ 
            border: 'none',
            background: 'transparent',
            fontSize: '1.5rem',
            fontWeight: 400,
            letterSpacing: '0.05em'
          }}>
            {formatDuration(recordingDuration)}
          </p>
        </div>
      </div>
    </div>
    </div>
  );
};

export default RecordingScreen;